import { ActivityLiteModel } from 'api-hooks/activity';
import { AgentModel } from 'api-hooks/agent';
import { AirlineModel } from 'api-hooks/airline';
import { BankAccountModel } from 'api-hooks/bank-account';
import { ChequeGiroModel } from 'api-hooks/cheque-giro';
import { CruiseRoomItemModel } from 'api-hooks/cruise';
import { CustomerModel } from 'api-hooks/customer';
import { EmployeeLiteModel } from 'api-hooks/employee';
import { GuestLiteModel } from 'api-hooks/guest';
import { PurchaseModel } from 'api-hooks/order/common/model';
import { PaymentMethodModel } from 'api-hooks/payment-method';
import { PreferenceTagItemModel } from 'api-hooks/preference-tag';
import { ProductVariantModel } from 'api-hooks/product/sales';
import { SaleModel } from 'api-hooks/sale/common';
import { HotelSaleHotelRoomModel } from 'api-hooks/sale/hotel';
import { PriceModel, TourFLightModel } from 'api-hooks/tour/tour-sale';
import { Expose, Transform, Type } from 'class-transformer';
import { RefundStatusEnum, ReleaseStatusEnum } from 'common/constants/enum';
import { ModelTransformer } from 'common/helpers/common';
import {
  CommonModel,
  CommonParamInput,
} from 'common/repositories/common.model';

// Enum

export enum RefundSalesTypeEnum {
  sales = 'general',
  tour = 'tour',
  product = 'product',
}

export enum SaleTransactionType {
  FLIGHT = 'flight',
  HOTEL = 'hotel',
  CRUISE = 'cruise',
  DOCUMENT = 'document',
  ACTIVITY = 'activity',
  PRODUCT = 'product',
  TOUR = 'tour',
}

// Model

export class RefundableHotelItemlModel extends CommonModel {
  detail: string;

  @Expose({ name: 'check_in' })
  @Type(() => Date)
  checkIn: Date;

  @Expose({ name: 'check_out' })
  @Type(() => Date)
  checkOut: Date;

  @Expose({ name: 'hotel_room' })
  @Type(() => HotelSaleHotelRoomModel)
  hotelRoom: HotelSaleHotelRoomModel;

  @Type(() => Number)
  night: number;

  @Type(() => Number)
  price: number;

  @Type(() => Number)
  room: number;
}

export class RefundableHotelAddOnsModel extends CommonModel {
  description: string;
  status: string;

  @Type(() => Number)
  price: number;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;
}

export class RefundableFlightItemModel extends CommonModel {
  route: string;
  status: string;
  ticket: any;
  time: string;
  detail: string;

  @Type(() => AirlineModel)
  airline: AirlineModel;

  @Type(() => Date)
  date: Date;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => PreferenceTagItemModel)
  preference: PreferenceTagItemModel;

  @Type(() => PreferenceTagItemModel)
  preferences: PreferenceTagItemModel[];

  @Type(() => Number)
  price: number;
}

export class RefundableFlightAddOnsModel extends CommonModel {
  route: string;
  status: string;
  description: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => Number)
  price: number;
}

export class RefundableTourItemModel extends CommonModel {
  note: string;
  status: string;

  @Expose({ name: 'bed_type' })
  bedType: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => Number)
  price: number;

  @Expose({ name: 'tour_flight' })
  @Type(() => TourFLightModel)
  tourFlight: TourFLightModel;

  @Expose({ name: 'tour_price' })
  @Type(() => PriceModel)
  tourPrice: PriceModel;
}

export class RefundableTourAddOnsModel extends CommonModel {
  status: string;
  description: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => Number)
  price: number;
}

export class RefundableProductItemModel extends CommonModel {
  description: string;
  status: string;

  @Expose({ name: 'complete_name' })
  completeName: string;

  @Type(() => Number)
  price: number;

  @Type(() => Number)
  qty: number;

  @Expose({ name: 'product_variant' })
  @Type(() => ProductVariantModel)
  productVariant: ProductVariantModel;
}

export class RefundableCruiseItemModel extends CommonModel {
  deck: string;
  description: string;
  preferences: { id: string; name: string }[];
  status: string;

  @Expose({ name: 'basic_fare' })
  @Type(() => Number)
  basicFare: number;

  @Expose({ name: 'cruise_room' })
  @Type(() => CruiseRoomItemModel)
  cruiseRoom: CruiseRoomItemModel;

  @Expose({ name: 'cruise_room_number' })
  cruiseRoomNumber: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Expose({ name: 'port_charge' })
  @Type(() => Number)
  portCharge: number;

  @Type(() => Number)
  tax: number;

  @Type(() => Number)
  tipping: number;
}

export class RefundableCruiseAddOnsModel extends CommonModel {
  status: string;
  description: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => Number)
  price: number;
}

export class RefundableActivityItemModel extends CommonModel {
  description: string;
  status: string;

  @Type(() => ActivityLiteModel)
  activity: ActivityLiteModel;

  @Type(() => Date)
  date: Date;

  @Type(() => Number)
  price: number;
}

export class RefundableActivityAddOnsModel extends CommonModel {
  status: string;
  description: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => Number)
  price: number;
}
export class RefundItemModel {
  id: string;
  description: any;
  refundable: any;

  @Expose({ name: 'refundable_type' })
  refundableType: string;
}

const itemTransformer = ({
  refundable,
  type,
}: {
  refundable: any;
  type: string;
}) => {
  switch (type) {
    case SaleTransactionType.ACTIVITY:
      return ModelTransformer(refundable, RefundableActivityItemModel);
    case SaleTransactionType.FLIGHT:
      return ModelTransformer(refundable, RefundableFlightItemModel);
    case SaleTransactionType.HOTEL:
      return ModelTransformer(refundable, RefundableHotelItemlModel);
    case SaleTransactionType.CRUISE:
      return ModelTransformer(refundable, RefundableCruiseItemModel);
    case SaleTransactionType.PRODUCT:
      return ModelTransformer(refundable, RefundableProductItemModel);
    case SaleTransactionType.TOUR:
      return ModelTransformer(refundable, RefundableTourItemModel);
    default:
      return refundable;
  }
};

const addonsTransformer = ({
  addonable,
  type,
}: {
  addonable: any;
  type: any;
}) => {
  switch (type) {
    case SaleTransactionType.ACTIVITY:
      return ModelTransformer(addonable, RefundableActivityAddOnsModel);
    case SaleTransactionType.FLIGHT:
      return ModelTransformer(addonable, RefundableFlightAddOnsModel);
    case SaleTransactionType.HOTEL:
      return ModelTransformer(addonable, RefundableHotelAddOnsModel);
    case SaleTransactionType.CRUISE:
      return ModelTransformer(addonable, RefundableCruiseAddOnsModel);
    case SaleTransactionType.TOUR:
      return ModelTransformer(addonable, RefundableTourAddOnsModel);
    default:
      return addonable;
  }
};

export class RefundSalePurchaseLiteModel {
  id: string;
  number: string;
  code: string;
  description: string;
  agent: string;
  status: string;

  @Expose({ name: 'ref_id' })
  refId: string;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Type(() => Number)
  total: number;

  @Expose({ name: 'transaction_type' })
  transactionType: string;

  @Expose({ name: 'payment_channel' })
  paymentChannel: string;
}

export class PaymentableModel extends CommonModel {
  name: string;

  //Cheque Giro Resource
  @Expose({ name: 'bank_account' })
  @Type(() => BankAccountModel)
  bankAccount?: BankAccountModel;

  @Expose({ name: 'cheque_giro' })
  @Type(() => ChequeGiroModel)
  chequeGiro?: ChequeGiroModel;

  @Expose({ name: 'available_at' })
  @Type(() => Date)
  availableAt?: Date;

  @Expose({ name: 'expired_at' })
  @Type(() => Date)
  expiredAt?: Date;

  @Expose({ name: 'issued_at' })
  @Type(() => Date)
  issuedAt?: Date;

  @Expose({ name: 'reference_number' })
  referenceNumber?: string;
}

export class ReleasePurchaseModel {
  id: string;
  status: ReleaseStatusEnum;

  @Type(() => Number)
  amount: number;

  @Type(() => AgentModel)
  agent: AgentModel;

  @Expose({ name: 'payment_method' })
  @Type(() => PaymentMethodModel)
  paymentMethod: PaymentMethodModel;

  @Type(() => PaymentableModel)
  paymentable: PaymentableModel;

  @Type(() => PurchaseModel)
  purchase: PurchaseModel;

  @Expose({ name: 'requested_at' })
  @Type(() => Date)
  requestedAt: Date;

  @Expose({ name: 'is_resolved' })
  isResolved: boolean;

  @Expose({ name: 'release_purchase_add_ons' })
  @Transform((params) => {
    const releasePurchaseAddOns = params.value.map((value) => ({
      id: value.id,
      description: value.description,
      addonableType: value.addonable_type,
      addonable: addonsTransformer({
        addonable: value.addonable,
        type: params.obj.purchase.transaction_type,
      }),
      refundPrice: parseFloat(value.refund_price || '0'),
    }));
    return releasePurchaseAddOns;
  })
  releasePurchaseAddOns: {
    addonableType: string;
    id: string;
    description: any;
    addonable:
      | RefundableTourAddOnsModel
      | RefundableHotelAddOnsModel
      | RefundableCruiseAddOnsModel
      | RefundableFlightAddOnsModel
      | RefundableActivityAddOnsModel;
    refundPrice: number;
  }[];

  @Expose({ name: 'release_purchase_items' })
  @Transform((params) => {
    const releasePurchaseItems = params.value.map((value) => ({
      id: value.id,
      description: value.description,
      refundableType: value.refundable_type,
      refundable: itemTransformer({
        refundable: value.refundable,
        type: params.obj.purchase.transaction_type,
      }),
      refundPrice: parseFloat(value.refund_price || '0'),
    }));
    return releasePurchaseItems;
  })
  releasePurchaseItems: {
    refundableType: string;
    id: string;
    description: any;
    refundable:
      | RefundableTourItemModel
      | RefundableHotelItemlModel
      | RefundableCruiseItemModel
      | RefundableFlightItemModel
      | RefundableActivityItemModel;
    refundPrice: number;
  }[];
}

export class CustomerBillingModel {
  id: string;
  name: string;
}

export class ReleaseSaleModel {
  @Type(() => Number)
  amount: number;

  id: string;
  status: ReleaseStatusEnum;

  @Type(() => CustomerModel)
  customer: CustomerModel;

  @Expose({ name: 'customer_billing' })
  @Type(() => CustomerBillingModel)
  customerBilling: CustomerBillingModel | null;

  @Type(() => PaymentableModel)
  paymentable: PaymentableModel;

  @Expose({ name: 'payment_method' })
  @Type(() => PaymentMethodModel)
  paymentMethod: PaymentMethodModel;

  @Type(() => SaleModel)
  sale: SaleModel;

  @Expose({ name: 'requested_at' })
  @Type(() => Date)
  requestedAt: Date;

  @Expose({ name: 'is_resolved' })
  isResolved: boolean;

  @Expose({ name: 'release_sale_items' })
  @Transform((params) => {
    const releaseSaleItems = params.value.map((value) => ({
      id: value.id,
      description: value.description,
      refundable: itemTransformer({
        refundable: value.refundable,
        type: params.obj.sale.transaction_type,
      }),
      refundableType: value.refundable_type,
      refundPrice: parseFloat(value.refund_price || '0'),
    }));

    return releaseSaleItems;
  })
  releaseSaleItems: {
    id: string;
    description: any;
    refundableType: string;
    refundable:
      | RefundableActivityItemModel
      | RefundableCruiseItemModel
      | RefundableTourItemModel
      | RefundableHotelItemlModel
      | RefundableFlightItemModel
      | RefundableProductItemModel;
    refundPrice: number;
  }[];

  @Expose({ name: 'release_sale_add_ons' })
  @Transform((params) => {
    const releaseSaleAddOns = params.value.map((value) => ({
      id: value.id,
      description: value.description,
      addonableType: value.addonable_type,
      addonable: addonsTransformer({
        addonable: value.addonable,
        type: params.obj.sale.transaction_type,
      }),
      refundPrice: parseFloat(value.refund_price || '0'),
    }));
    return releaseSaleAddOns;
  })
  releaseSaleAddOns: {
    id: string;
    description: any;
    addonableType: string;
    addonable:
      | RefundableTourAddOnsModel
      | RefundableHotelAddOnsModel
      | RefundableCruiseAddOnsModel
      | RefundableFlightAddOnsModel
      | RefundableActivityAddOnsModel;
    refundPrice: number;
  }[];
}

export class RefundModel {
  id: string;
  reason: string;
  status: RefundStatusEnum;
  number: string;

  @Expose({ name: 'ref_id' })
  refId: string;

  @Expose({ name: 'requested_at' })
  @Type(() => Date)
  requestedAt: Date;

  @Expose({ name: 'release_purchase' })
  @Type(() => ReleasePurchaseModel)
  releasePurchase: ReleasePurchaseModel | null;

  @Expose({ name: 'release_sale' })
  @Type(() => ReleaseSaleModel)
  releaseSale: ReleaseSaleModel;

  @Expose({ name: 'waiting_id' })
  waitingId: string;

  @Expose({ name: 'is_resolved' })
  isResolved: boolean;
}

export class RefundLiteModel {
  id: string;
  agent: string;
  reason: string;
  customer: string;
  number: string;

  @Expose({ name: 'agent_refund' })
  agentRefund: string;

  @Expose({ name: 'customer_refund' })
  customerRefund: string;

  @Expose({ name: 'invoice_number' })
  invoiceNumber: string;

  @Expose({ name: 'order_number' })
  orderNumber: string;

  @Expose({ name: 'release_agent_status' })
  releaseAgentStatus: string;

  @Expose({ name: 'release_customer_status' })
  releaseCustomerStatus: string;

  @Expose({ name: 'requested_at' })
  @Type(() => Date)
  requestedAt: Date;

  @Expose({ name: 'created_by' })
  @Type(() => EmployeeLiteModel)
  createdBy: EmployeeLiteModel;
}

//query
export type getRefundsInput = {
  params: CommonParamInput & {
    status?: string;
  };
};

export type getRefundInput = {
  id: string;
};

export type getRefundSalePurchaseInput = {
  id: string;
  params?: CommonParamInput;
};

export type getRefundSalesInput = {
  params?: CommonParamInput & {
    type?: string;
  };
};

export type getRefundSaleInput = {
  id: string;
};

//mutation

export type ChequeNoteInputModel = {
  chequeGiroId?: string;
  bankAccountId?: string;
  referenceNumber?: string;
  issuedAt?: Date;
  availableAt?: Date;
  expiredAt?: Date;
};

export type ReleaseSaleInput = {
  refId: string | null;
  customerId: string | null;
  customerBillingId: string | null;
  amount: number;
  paymentMethodId: string | null;
  paymentableId: string | null;
  chequeNote?: ChequeNoteInputModel | null;
  items: { id: string; refundPrice: number }[];
  addOns: { id: string; refundPrice: number }[];
};

export type ReleasePurchaseInput = {
  refId: string | null;
  agentId: string | null;
  amount: number;
  paymentMethodId: string | null;
  paymentableId: string | null;
  chequeNote?: ChequeNoteInputModel | null;
  items: { id: string; refundPrice: number }[];
  addOns: { id: string; refundPrice: number }[];
};

export type RefundMutationInput = {
  reason: string;
  requestedAt: Date;
  releaseSale: ReleaseSaleInput;
  releasePurchase?: ReleasePurchaseInput | null;
};

export type RefundMutationUpdateInput = {
  reason: string;
  requestedAt: Date;
};
export type RefundMutationUpdate = {
  id: string;
  body: RefundMutationUpdateInput;
};
export type RefundMutationDelete = {
  id: string;
};

//update

export type RefundMutationUpdateReleasePurchaseInput = {
  agentId: string | null;
  amount: number;
  paymentMethodId: string | null;
  paymentableId: string | null;
  chequeNote: {
    chequeGiroId?: string;
    bankAccountId?: string;
    referenceNumber?: string;
    issuedAt?: Date;
    availableAt?: Date;
    expiredAt?: Date;
  } | null;
};

export type RefundMutationUpdateReleasePurchase = {
  id: string;
  body: RefundMutationUpdateReleasePurchaseInput;
};

export type RefundMutationUpdateReleaseSaleInput = {
  customerId: string | null;
  amount: number;
  paymentMethodId: string | null;
  paymentableId: string | null;
  chequeNote: {
    chequeGiroId?: string;
    bankAccountId?: string;
    referenceNumber?: string;
    issuedAt?: Date;
    availableAt?: Date;
    expiredAt?: Date;
  } | null;
};

export type RefundMutationUpdateReleaseSale = {
  id: string;
  body: RefundMutationUpdateReleaseSaleInput;
};
